import type { FlowMode } from "@/components/company-census/types";
import type { AnalyticsIdentityInput } from "@cakewalk/frontend-api";
import { fetchAuthSession } from "aws-amplify/auth";
import { useEffect, useState } from "react";

/**
 * Derive the `<AnalyticsIdentity>` input for the census flow.
 *
 * Guests stay anonymous. For a logged-in client we read the Cognito ID-token
 * claims (`sub` + the org claim) so PostHog can attach the person profile and
 * the `organization` group — the same keys Cockpit/Wallet use, so sessions
 * stitch across apps. Only the sanctioned identity channels carry the email
 * (the dedicated `email` arg); no other PII is sent.
 */
export function useCensusAnalyticsIdentity(
  mode: FlowMode | null,
  accountEmail: string,
): AnalyticsIdentityInput {
  const [claims, setClaims] = useState<{ userId?: string; organizationId?: string }>({});

  useEffect(() => {
    if (mode !== "authenticated") {
      setClaims({});
      return;
    }
    let cancelled = false;
    fetchAuthSession()
      .then((session) => {
        if (cancelled) return;
        const payload = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
        if (!payload) return;
        const sub = typeof payload.sub === "string" ? payload.sub : undefined;
        const orgClaim = payload["custom:organization_id"] ?? payload["custom:company_id"];
        const organizationId =
          typeof orgClaim === "string" && orgClaim.length > 0 ? orgClaim : undefined;
        setClaims({ userId: sub, organizationId });
      })
      .catch(() => {
        if (!cancelled) setClaims({});
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  if (mode !== "authenticated") {
    return { isAuthenticated: false, enableAliasFromEmail: false };
  }
  return {
    isAuthenticated: true,
    userId: claims.userId,
    email: accountEmail || undefined,
    organizationId: claims.organizationId,
    enableAliasFromEmail: false,
  };
}
